(() => {
  const GROUPS = [
    { key: 'Use', label: 'Use' },
    { key: 'Product Type', label: 'Product type' },
    { key: 'Audience', label: 'For', map: { Adults: 'Adults', Children: 'Kids' } },
    { key: 'Badge', label: 'Feature' },
    { key: 'Screen Size', label: 'Screen size', numeric: true },
    { key: 'Screen Ratio', label: 'Aspect ratio' },
    { key: 'Mounting', label: 'Mounting' },
    { key: 'Filter Type', label: 'Filter type' },
    { key: 'Fit Method', label: 'Fit method' },
    { key: 'MacBook Model', label: 'MacBook model' },
    { key: 'Base', label: 'Socket' },
    { key: 'Light Variant', label: 'Light mode' }
  ];

  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]));
  const parseTags = (raw) => (raw || '').split('||').map((x) => x.trim()).filter(Boolean);
  const tagValue = (tag, key) => tag.startsWith(key + ': ') ? tag.slice(key.length + 2).trim() : null;
  const numericValue = (v) => {
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
  };

  function init(root) {
    const grid = document.getElementById('product-grid');
    if (!grid || !root) return;

    const panel = root.querySelector('[data-nb-filter-panel]');
    const groupsEl = root.querySelector('[data-nb-filter-groups]');
    const loadingEl = root.querySelector('[data-nb-filter-loading]');
    const countEl = root.querySelector('[data-nb-result-count]');
    const badgeEl = root.querySelector('[data-nb-filter-count]');
    const backdrop = root.querySelector('[data-nb-filter-backdrop]');
    const toggle = root.querySelector('[data-nb-filter-toggle]');
    const sort = root.querySelector('[data-nb-sort]');
    const chips = root.querySelector('[data-nb-chips]');
    const applyButton = root.querySelector('[data-nb-filter-apply]');
    const nativePagination = grid.closest('.collection')?.querySelector('.pagination-wrapper');
    const originalGridHtml = grid.innerHTML;
    const originalPaginationHtml = nativePagination?.outerHTML || '';
    const perPage = Number(root.dataset.perPage || 12);
    let allProducts = null;
    let currentFiltered = null;
    let currentPage = 1;
    let customPagination = null;
    let priceBounds = null;

    const closePanel = () => {
      panel.hidden = true;
      backdrop.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('noblue-filters-open');
    };

    const openPanel = async () => {
      panel.hidden = false;
      backdrop.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('noblue-filters-open');
      if (!allProducts) await loadProducts();
    };

    root.querySelectorAll('[data-nb-filter-close]').forEach((b) => b.addEventListener('click', closePanel));
    applyButton?.addEventListener('click', closePanel);
    backdrop.addEventListener('click', closePanel);
    toggle.addEventListener('click', () => panel.hidden ? openPanel() : closePanel());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) closePanel(); });

    sort?.addEventListener('change', () => {
      const u = new URL(window.location.href);
      u.searchParams.set('sort_by', sort.value);
      u.searchParams.delete('page');
      window.location.assign(u.toString());
    });

    async function loadProducts() {
      loadingEl.hidden = false;
      const currentNodes = [...grid.querySelectorAll(':scope > li[data-noblue-card="true"]')];
      const byHandle = new Map(currentNodes.map((n) => [n.dataset.productHandle, n.outerHTML]));

      const pageLinks = [...document.querySelectorAll('.pagination-wrapper a[href*="page="]')];
      let maxPage = 1;
      for (const a of pageLinks) {
        try {
          const n = Number(new URL(a.href, window.location.origin).searchParams.get('page') || 1);
          if (n > maxPage) maxPage = n;
        } catch (_) {}
      }

      const currentUrl = new URL(window.location.href);
      const currentPageNumber = Number(currentUrl.searchParams.get('page') || 1);
      const requests = [];
      for (let page = 1; page <= maxPage; page++) {
        if (page === currentPageNumber) continue;
        const u = new URL(window.location.href);
        u.searchParams.set('page', String(page));
        requests.push(fetch(u.toString(), { credentials: 'same-origin' }).then((r) => r.text()));
      }

      try {
        const htmlPages = await Promise.all(requests);
        for (const html of htmlPages) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('#product-grid > li[data-noblue-card="true"]').forEach((n) => {
            byHandle.set(n.dataset.productHandle, n.outerHTML);
          });
        }
      } catch (e) {
        console.warn('NoBlue filters: could not load all collection pages', e);
      }

      const parser = new DOMParser();
      allProducts = [...byHandle.values()].map((html) => {
        const doc = parser.parseFromString('<ul>' + html + '</ul>', 'text/html');
        const node = doc.querySelector('li[data-noblue-card="true"]');
        return {
          html,
          handle: node?.dataset.productHandle || '',
          title: node?.dataset.productTitle || '',
          tags: parseTags(node?.dataset.productTags || ''),
          available: node?.dataset.productAvailable === 'true',
          priceMin: Number(node?.dataset.productPriceMin || 0),
          priceMax: Number(node?.dataset.productPriceMax || 0),
          created: Number(node?.dataset.productCreated || 0)
        };
      }).filter((p) => p.handle);

      buildGroups();
      loadingEl.hidden = true;
      updateApplyText(allProducts.length);
    }

    function optionCount(groupKey, value) {
      if (groupKey === '__availability') {
        return allProducts.filter((p) => (p.available ? 'Available' : 'Sold out') === value).length;
      }
      return allProducts.filter((p) => p.tags.includes(groupKey + ': ' + value)).length;
    }

    function buildGroups() {
      groupsEl.innerHTML = '';

      const states = new Set(allProducts.map((p) => p.available ? 'Available' : 'Sold out'));
      if (states.size > 1) {
        const wrap = document.createElement('details');
        wrap.className = 'noblue-filter-group';
        wrap.dataset.group = '__availability';
        wrap.innerHTML = '<summary>Availability</summary><div class="noblue-filter-group__options">' +
          ['Available','Sold out'].map((v) => optionHtml('__availability', v, v, optionCount('__availability', v))).join('') +
          '</div>';
        groupsEl.appendChild(wrap);
      }

      const prices = allProducts.map((p) => p.priceMin).filter((n) => Number.isFinite(n));
      if (prices.length) {
        const min = Math.min(...prices), max = Math.max(...prices);
        if (max > min) {
          priceBounds = { min, max };
          const wrap = document.createElement('details');
          wrap.className = 'noblue-filter-group';
          wrap.dataset.group = '__price';
          wrap.innerHTML = '<summary>Price</summary><div class="noblue-filter-price">' +
            '<label>Min<input type="number" step="0.01" min="' + (min/100).toFixed(2) + '" max="' + (max/100).toFixed(2) + '" value="' + (min/100).toFixed(2) + '" data-nb-price-min></label>' +
            '<label>Max<input type="number" step="0.01" min="' + (min/100).toFixed(2) + '" max="' + (max/100).toFixed(2) + '" value="' + (max/100).toFixed(2) + '" data-nb-price-max></label></div>';
          groupsEl.appendChild(wrap);
        }
      }

      for (const group of GROUPS) {
        const values = new Set();
        allProducts.forEach((p) => p.tags.forEach((tag) => {
          const v = tagValue(tag, group.key);
          if (v) values.add(v);
        }));
        if (values.size < 2) continue;

        const sorted = [...values].sort((a,b) => group.numeric ? numericValue(a) - numericValue(b) : a.localeCompare(b, document.documentElement.lang));
        const wrap = document.createElement('details');
        wrap.className = 'noblue-filter-group';
        wrap.dataset.group = group.key;
        wrap.innerHTML = '<summary>' + esc(group.label) + '</summary><div class="noblue-filter-group__options">' +
          sorted.map((value) => optionHtml(group.key, value, group.map?.[value] || value, optionCount(group.key, value))).join('') +
          '</div>';
        groupsEl.appendChild(wrap);
      }

      groupsEl.addEventListener('change', applyFilters);
      groupsEl.addEventListener('input', (e) => {
        if (e.target.matches('[data-nb-price-min],[data-nb-price-max]')) {
          window.clearTimeout(groupsEl._nbTimer);
          groupsEl._nbTimer = window.setTimeout(applyFilters, 250);
        }
      });
    }

    function optionHtml(group, value, label, count) {
      const id = 'nbf-' + (group + '-' + value).replace(/\W+/g,'-').toLowerCase();
      return '<label class="noblue-filter-option" for="' + esc(id) + '">' +
        '<input id="' + esc(id) + '" type="checkbox" data-nb-filter-value data-group="' + esc(group) + '" value="' + esc(value) + '">' +
        '<span class="noblue-filter-option__label">' + esc(label) + '</span>' +
        '<span class="noblue-filter-option__count">' + count + '</span>' +
      '</label>';
    }

    function selectedGroups() {
      const selected = new Map();
      groupsEl.querySelectorAll('[data-nb-filter-value]:checked').forEach((input) => {
        if (!selected.has(input.dataset.group)) selected.set(input.dataset.group, []);
        selected.get(input.dataset.group).push(input.value);
      });
      return selected;
    }

    function applyFilters() {
      if (!allProducts) return;
      const selected = selectedGroups();
      const minInput = groupsEl.querySelector('[data-nb-price-min]');
      const maxInput = groupsEl.querySelector('[data-nb-price-max]');
      const minPrice = minInput ? Math.round(Number(minInput.value || 0) * 100) : null;
      const maxPrice = maxInput ? Math.round(Number(maxInput.value || 0) * 100) : null;
      const priceActive = priceBounds && (minPrice > priceBounds.min || maxPrice < priceBounds.max);

      currentFiltered = allProducts.filter((p) => {
        for (const [group, values] of selected) {
          if (group === '__availability') {
            const state = p.available ? 'Available' : 'Sold out';
            if (!values.includes(state)) return false;
            continue;
          }
          if (!values.some((value) => p.tags.includes(group + ': ' + value))) return false;
        }
        if (priceActive && (p.priceMin < minPrice || p.priceMin > maxPrice)) return false;
        return true;
      });

      currentPage = 1;
      const activeValues = [...selected.values()].reduce((n, a) => n + a.length, 0) + (priceActive ? 1 : 0);
      badgeEl.textContent = String(activeValues);
      badgeEl.hidden = activeValues === 0;
      countEl.textContent = currentFiltered.length + (currentFiltered.length === 1 ? ' product' : ' products');
      updateApplyText(currentFiltered.length);
      renderChips(selected, priceActive, minPrice, maxPrice);

      if (activeValues === 0) {
        grid.innerHTML = originalGridHtml;
        removeCustomPagination();
        restoreNativePagination();
        return;
      }
      renderFiltered(currentFiltered, currentPage);
    }

    function renderChips(selected, priceActive, minPrice, maxPrice) {
      const parts = [];
      for (const [group, values] of selected) {
        for (const value of values) {
          const label = group === '__availability' ? value : value;
          parts.push('<button type="button" class="noblue-filter-chip" data-chip-group="' + esc(group) + '" data-chip-value="' + esc(value) + '">' + esc(label) + '<span class="noblue-filter-chip__x">×</span></button>');
        }
      }
      if (priceActive) {
        parts.push('<button type="button" class="noblue-filter-chip" data-chip-price>Price: ' + (minPrice/100).toFixed(2) + '–' + (maxPrice/100).toFixed(2) + '<span class="noblue-filter-chip__x">×</span></button>');
      }
      chips.innerHTML = parts.join('');
      chips.hidden = parts.length === 0;
      chips.querySelectorAll('[data-chip-group]').forEach((b) => b.addEventListener('click', () => {
        const input = groupsEl.querySelector('[data-nb-filter-value][data-group="' + CSS.escape(b.dataset.chipGroup) + '"][value="' + CSS.escape(b.dataset.chipValue) + '"]');
        if (input) input.checked = false;
        applyFilters();
      }));
      chips.querySelector('[data-chip-price]')?.addEventListener('click', () => {
        if (priceBounds) {
          const min = groupsEl.querySelector('[data-nb-price-min]');
          const max = groupsEl.querySelector('[data-nb-price-max]');
          if (min) min.value = (priceBounds.min/100).toFixed(2);
          if (max) max.value = (priceBounds.max/100).toFixed(2);
        }
        applyFilters();
      });
    }

    function updateApplyText(count) {
      if (applyButton) applyButton.textContent = 'Show ' + count + (count === 1 ? ' product' : ' products');
    }

    function renderFiltered(filtered, page) {
      const pages = Math.max(1, Math.ceil(filtered.length / perPage));
      currentPage = Math.max(1, Math.min(page, pages));
      const start = (currentPage - 1) * perPage;
      grid.innerHTML = filtered.slice(start, start + perPage).map((p) => p.html).join('');
      hideNativePagination();
      renderCustomPagination(filtered, pages);
    }

    function renderCustomPagination(filtered, pages) {
      removeCustomPagination();
      if (pages <= 1) return;
      customPagination = document.createElement('div');
      customPagination.className = 'noblue-filters__pagination';
      for (let p = 1; p <= pages; p++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = String(p);
        if (p === currentPage) b.setAttribute('aria-current', 'page');
        b.addEventListener('click', () => {
          renderFiltered(filtered, p);
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        customPagination.appendChild(b);
      }
      grid.parentElement.appendChild(customPagination);
    }

    function removeCustomPagination() {
      customPagination?.remove();
      customPagination = null;
    }
    function hideNativePagination() {
      const p = grid.closest('.collection')?.querySelector('.pagination-wrapper');
      if (p) p.hidden = true;
    }
    function restoreNativePagination() {
      const p = grid.closest('.collection')?.querySelector('.pagination-wrapper');
      if (p) p.hidden = false;
      else if (originalPaginationHtml) grid.parentElement.insertAdjacentHTML('beforeend', originalPaginationHtml);
    }

    root.querySelector('[data-nb-filter-clear]')?.addEventListener('click', () => {
      groupsEl.querySelectorAll('[data-nb-filter-value]').forEach((i) => { i.checked = false; });
      if (priceBounds) {
        const min = groupsEl.querySelector('[data-nb-price-min]');
        const max = groupsEl.querySelector('[data-nb-price-max]');
        if (min) min.value = (priceBounds.min/100).toFixed(2);
        if (max) max.value = (priceBounds.max/100).toFixed(2);
      }
      applyFilters();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-noblue-filters]').forEach(init);
  });
})();